<template>
  <a-button block type="text" class="table-cell__actions-btn" @click="handleShowModal">
    <template #icon>
      <PieChartOutlined
        :style="{
          fontSize: '16px',
        }"
      />
    </template>

    <a-modal
      v-model:visible="visible"
      centered
      :width="modalWidth"
      :title="`${strategy.name} - ${this.strategy.type_strategy_name}`"
      :keyboard="false"
      :body-style="{ padding: '16px 20px' }"
      :mask-closable="false"
      class="strategy__dashboard-modal"
      @cancel="handleClose"
    >
      <div v-if="data">
        <predictive-dashboard v-if="isPredictive" :data="data" />
        <messaging-dashboard
          v-else-if="isMessaging"
          :data="data"
          :type="this.strategy.type_strategy"
        />
        <IvrDashboard v-else-if="isIvr" :data="data" />
        <EmailDashboard v-else-if="isEmail" :data="data" />
        <IvrReturnDashboard
          v-else-if="isIvrReturn"
          :data="data" 
          :type="strategy.type_strategy"
        />
        <progressive-dashboard v-else :data="data" :type-assignment="strategy.type_assignment" />
      </div>
      <BounceCard v-if="isEmail && showBounceEmail" />
      <template #footer>
        <div class="modal__footer">
          <!-- <span v-if="isMessaging" class="modal__caption">
            Se utilizarán 480 mensajes SMS para esta estrategia
          </span> -->

          <div class="footer__update">
            <a-button type="primary" ghost @click="handleUpdate">Actualizar datos</a-button>

            <p class="footer__button" v-if="isSomeStrategyRunning">
              Actualizando datos en {{ countToUpdate }} segundos
            </p>
          </div>

          <div>
            <a-tooltip :overlayStyle="{ width: '175px' }" v-if="isGlobalDownloading || loading">
              <template #title>
                <span v-if="isGlobalDownloading">Tiene una descarga pendiente</span>
                <span v-if="loading"> Su descarga estará lista en unos instantes </span>
              </template>

              <a-button
                :loading="loading"
                :disabled="isGlobalDownloading"
                @click="handleDownloadStrategy"
              >
                Descargar detalle
              </a-button>
            </a-tooltip>
            <a-button
              v-else
              :loading="loading"
              :disabled="isGlobalDownloading"
              @click="handleDownloadStrategy"
            >
              Descargar detalle
            </a-button>
            <a-button type="primary" @click="handleClose">Cerrar</a-button>
          </div>
        </div>
      </template>
    </a-modal>
    <p>Ver dashboard</p>
  </a-button>
</template>

<script>
import { inject, computed, ref, provide } from "vue";
import { PieChartOutlined } from "@ant-design/icons-vue";
import _ from "lodash";

import { StrategyType } from "@/app/shared/utils/enums";
import { fetchDashboardData } from "@/app/strategies/services";
import { downloadFile } from "@/app/shared/utils/file";
import { EventManagement } from "@/app/shared/utils/enums";

import PredictiveDashboard from "@/app/strategies/components/modals/dashboard/PredictiveDashboard.vue";
import ProgressiveDashboard from "@/app/strategies/components/modals/dashboard/ProgressiveDashboard.vue";
import EmailDashboard from "@/app/strategies/components/modals/dashboard/EmailDashboard.vue";
import MessagingDashboard from "@/app/strategies/components/modals/dashboard/MessagingDashboard.vue";
import IvrDashboard from "./dashboard/IvrDashboard.vue";
import IvrReturnDashboard from "./dashboard/IvrReturnDashboard.vue";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";
import BounceCard from "@/app/strategies/components/cards/BounceCard.vue";
import { TypeAssignment } from "@/app/shared/utils/enums";

export default {
  components: {
    PredictiveDashboard,
    ProgressiveDashboard,
    MessagingDashboard,
    PieChartOutlined,
    IvrDashboard,
    EmailDashboard,
    BounceCard,
    IvrReturnDashboard,
  },
  props: {
    strategy: {
      required: true,
      type: Object,
    },
  },
  setup(props) {
    const TIME_TO_UPDATE = inject("TIME_TO_UPDATE");
    const clientLoading = inject("clientLoading");
    const serverLoading = inject("serverLoading");
    const countToUpdate = inject("countToUpdate");
    const isSomeStrategyRunning = inject("isSomeStrategyRunning");
    const fetchTableData = inject("fetchTableData");

    const { isGlobalDownloading } = useGlobalDownloadState();
    const loading = computed(() => clientLoading.value || serverLoading.value);
    const strategyProgress = computed(() => {
      const { remaining_phones, total_phones } = props.strategy;

      return `${remaining_phones}/${total_phones}`;
    });

    const selectedAgentsStates = ref([
      0,
      EventManagement.Waiting,
      EventManagement.Ringing,
      EventManagement.Talking,
      EventManagement.Acw,
    ]);

    provide("selectedAgentsStates", selectedAgentsStates);
    provide("strategyProgress", strategyProgress);

    return {
      loading,
      clientLoading,
      serverLoading,
      countToUpdate,
      isSomeStrategyRunning,
      selectedAgentsStates,
      fetchTableData,
      TIME_TO_UPDATE,
      isGlobalDownloading,
    };
  },
  data() {
    return {
      visible: false,
      data: null,
      stopTime: null,
      showBounceEmail: false,
    };
  },
  computed: {
    isPredictive() {
      const strategyType = this.strategy.type_strategy;
      return strategyType === StrategyType.predictive;
    },
    isEmail() {
      const strategyType = this.strategy.type_strategy;
      console.log(strategyType, "strategyType");
      return strategyType === StrategyType.email;
    },
    isIvr() {
      return this.strategy.type_strategy === StrategyType.ivr;
    },
    isIvrReturn() {
      return this.strategy.type_strategy === StrategyType.ivrReturn;
    },
    isMessaging() {
      const strategyType = this.strategy.type_strategy;
      return strategyType === StrategyType.sms || strategyType === StrategyType.whatsapp;
    },
    modalWidth() {
      if (this.isMessaging) return "800px";
      else if (this.isEmail) return "70vw";
      else if (this.isPredictive) {
        return "85vw";
      }

      if (this.strategy.type_assignment == TypeAssignment.AssignedUser) {
        return "85vw";
      } else {
        return "65vw";
      }
    },
  },
  methods: {
    async fetchData() {
      this.data = await fetchDashboardData(
        this.strategy.id,
        this.isPredictive ? this.selectedAgentsStates : []
      );
      console.log(this.data, "dataaaa dashboard");
    },
    async handleDownloadStrategy() {
      this.clientLoading = true;
      const response = await this.$store.dispatch("downloadStrategy", this.strategy.id);
      downloadFile(response, `${this.strategy.name}.xlsx`, { useResponseFileName: true });
      this.clientLoading = false;
    },
    async handleShowModal() {
      await this.fetchData();
      this.visible = true;
    },
    handleUpdate() {
      this.fetchData();
      this.fetchTableData({ showLoader: false });
      this.countToUpdate = this.TIME_TO_UPDATE;
    },
    handleClose() {
      this.visible = false;
    },
  },
  watch: {
    selectedAgentsStates: _.debounce(function () {
      this.fetchData();
    }, 500),
    countToUpdate(newCounterValue) {
      if (newCounterValue === 1 && this.visible && this.isSomeStrategyRunning) this.fetchData();
    },
  },
};
</script>

<style lang="sass" scoped>
.modal
  &__footer
    display: flex
    align-items: center
    justify-content: space-between

  &__caption
    width: 100%
    color: $gray-7
    text-align: left

.footer
  &__button
    margin: 0
    color: $gray-7
    font-style: italic

  &__update
    display: flex
    align-items: center
    gap: 12px
</style>

<style>
.strategy__dashboard-modal .modal__footer .ant-btn-loading {
  pointer-events: auto !important;
  cursor: default;
}
</style>
